import axios from 'axios';
import { captureException } from './sentry';

const availableFonts = [
  { value: "Arial", label: "Arial" },
  { value: "Verdana", label: "Verdana" },
  { value: "Helvetica", label: "Helvetica" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Georgia", label: "Georgia" },
  { value: "Poppins", label: "Poppins (Modern)" },
  { value: "Montserrat", label: "Montserrat (Modern)" }
];

// Sanitize the configured base URL. The dotenv version bundled with
// react-scripts 5 does NOT strip inline comments, so a line like
// `REACT_APP_API_URL=https://x/api #localhost` bakes the whole string
// (URL + space + comment) into the value, producing malformed request URLs.
// Trim whitespace and drop anything from the first whitespace/`#` onward.
const sanitizeBaseUrl = (value) =>
  String(value || '')
    .trim()
    .split(/\s|#/)[0]
    .replace(/\/+$/, '');

export const API_BASE_URL =
  sanitizeBaseUrl(process.env.API_BASE_URL || process.env.REACT_APP_API_URL) || '/api';

console.log('API_BASE_URL configured as:', API_BASE_URL);

// Report unexpected API failures to Sentry (5xx + network). Expected 4xx stay local.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const isNetwork = !error?.response;
      const isServerError = typeof status === 'number' && status >= 500;
      if (isNetwork || isServerError) {
        captureException(error, {
          tags: {
            source: 'axios',
            ...(status ? { http_status: String(status) } : { http_status: 'network' }),
          },
          extra: {
            url: error?.config?.url,
            method: error?.config?.method,
            message: error?.message,
          },
        });
      }
    } catch (_) {
      // never break the request chain because of telemetry
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const registerUser = (data) =>
  axios.post(`${API_BASE_URL}/auth/register`, data);

export const loginUser = (data) =>
  axios.post(`${API_BASE_URL}/auth/login`, data);

// OTP APIs
export const sendOTP = (phone) =>
  axios.post(`${API_BASE_URL}/otp/send-otp`, { phone });

export const verifyOTP = (phone, otp, name, userPersonas) =>
  axios.post(`${API_BASE_URL}/otp/verify-otp`, { phone, otp, name, userPersonas });

export const completeSignup = (phone, name, userPersonas) =>
  axios.post(`${API_BASE_URL}/otp/complete-signup`, { phone, name, userPersonas });

export const resendOTP = (phone) =>
  axios.post(`${API_BASE_URL}/otp/resend-otp`, { phone });

// Firebase Auth API
export const verifyFirebaseToken = (firebaseToken, phoneNumber, name, influencerCode, userPersonas) =>
  axios.post(`${API_BASE_URL}/auth/firebase-verify`, {
    firebaseToken,
    phoneNumber,
    name,
    userPersonas,
    ...(influencerCode ? { influencerCode } : {}),
  });

// Brand APIs
export const getBrands = (token) =>
  axios.get(`${API_BASE_URL}/brands`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const createBrand = (data, token) =>
  axios.post(`${API_BASE_URL}/brands`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateBrand = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/brands/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deleteBrand = (id, token) =>
  axios.delete(`${API_BASE_URL}/brands/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Kit APIs
export const getKits = (token, page = 1, limit = 10, prefetch = false) =>
  axios.get(`${API_BASE_URL}/kits?page=${page}&limit=${limit}&prefetch=${prefetch}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getSubscribedFeed = (token, page = 1, limit = 20) =>
  axios.get(`${API_BASE_URL}/kits/subscribed-feed?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const createKit = (data, token) =>
  axios.post(`${API_BASE_URL}/kits`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateKit = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/kits/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deleteKit = (id, token) =>
  axios.delete(`${API_BASE_URL}/kits/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Get single kit by id
export const getKitById = (id, token) =>
  axios.get(`${API_BASE_URL}/kits/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Moodboard APIs
export const getMoodboards = (token, page = 1, limit = 50) =>
  axios.get(`${API_BASE_URL}/moodboards?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const createMoodboard = (data, token) =>
  axios.post(`${API_BASE_URL}/moodboards`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateMoodboard = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/moodboards/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deleteMoodboard = (id, token) =>
  axios.delete(`${API_BASE_URL}/moodboards/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Scene Suggestion API
export const generateSuggestedScene = async ({ category, description, targetAudience, tone }) => {
  const token = localStorage.getItem('token');
  const res = await axios.post(`${API_BASE_URL}/scene/suggested`, {
    category,
    description,
    targetAudience,
    tone,
  }, {
    headers: { Authorization: `Bearer ${token}` }
  }); 
  return res.data;
};

// Access Request APIs
export const submitAccessRequest = (data) =>
  axios.post(`${API_BASE_URL}/access-requests`, data);

export const getAccessRequests = (token, params = {}) =>
  axios.get(`${API_BASE_URL}/access-requests`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });

export const updateAccessRequestStatus = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/access-requests/${id}/status`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deleteAccessRequest = (id, token) =>
  axios.delete(`${API_BASE_URL}/access-requests/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Support Request APIs
export const createSupportRequest = (data, token = null) =>
  axios.post(`${API_BASE_URL}/support-requests`, data, {
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {})
  });

export const getSupportRequests = (token, params = {}) =>
  axios.get(`${API_BASE_URL}/support-requests`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });

export const updateSupportRequest = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/support-requests/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Feedback APIs
export const getMyFeedbackStatus = (token) =>
  axios.get(`${API_BASE_URL}/feedback/me`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const createFeedback = (data, token) =>
  axios.post(`${API_BASE_URL}/feedback`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getAllFeedback = (token, params = {}) =>
  axios.get(`${API_BASE_URL}/feedback`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });

// Product analytics
export const getFabricMaterialStatsToday = (token) =>
  axios.get(`${API_BASE_URL}/products/stats/today`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Kit analytics
export const getAICreationsStatsToday = (token) =>
  axios.get(`${API_BASE_URL}/kits/stats/today`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getDailyActiveUsers = (token) =>
  axios.get(`${API_BASE_URL}/kits/stats/daily-active-users`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// User Profile APIs
export const getUserProfile = (token) =>
  axios.get(`${API_BASE_URL}/users/profile`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateUserProfile = (data, token) =>
  axios.patch(`${API_BASE_URL}/users/profile`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const checkPublicUsernameAvailability = (username, token, options = {}) =>
  axios.get(`${API_BASE_URL}/users/public-username/check`, {
    params: { username },
    headers: { Authorization: `Bearer ${token}` },
    signal: options.signal,
  });

/** Parse username availability API response without treating missing fields as "taken". */
export function parseUsernameCheckPayload(response) {
  const body = response?.data;
  if (!body || typeof body !== 'object') {
    return { available: null, message: 'Unexpected server response.' };
  }

  const payload = body.data && typeof body.data === 'object' ? body.data : body;

  if (payload.available === true) {
    return {
      available: true,
      message: payload.message || 'Username is available.',
    };
  }

  if (payload.available === false) {
    return {
      available: false,
      message: payload.message || 'This username is not available.',
    };
  }

  return {
    available: null,
    message: body.message || payload.message || 'Could not verify username availability.',
  };
}

// User Settings APIs
export const getUserSettings = (token) =>
  axios.get(`${API_BASE_URL}/users/settings`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateUserSettings = (settings, token) =>
  axios.patch(`${API_BASE_URL}/users/settings`, { settings }, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateVirtualTryOnSetting = (enabled, token) =>
  axios.patch(`${API_BASE_URL}/users/settings/virtual-tryon`, { enabled }, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Admin: verify vendor for public directory
export const updateVendorVerified = (userId, verified, token) =>
  axios.patch(`${API_BASE_URL}/users/${userId}/vendor-verified`, { verified }, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const resendWelcomeEmail = (userId, token) =>
  axios.post(`${API_BASE_URL}/users/${userId}/welcome-email`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const changePassword = (data, token) =>
  axios.post(`${API_BASE_URL}/users/change-password`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Purchase Credits API
export const purchaseCredits = (packageId, token) =>
  axios.post(`${API_BASE_URL}/users/purchase-credits`, { packageId }, {
    headers: { Authorization: `Bearer ${token}` }
  }); 

async function postQueuedGeneration(url, data, token, maxWaitMs = 8 * 60 * 1000) {
  const { postGenerationJob } = await import('./api/generationJob.api');
  const body = await postGenerationJob(url, data, { token, maxWaitMs });
  return { data: body };
}

// Magic Edit API
export const magicEdit = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit`, data, token);

// Magic Edit V2 API (using Nano Banana)
export const magicEditV2 = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/v2`, data, token);

// Pattern resizer — rescale the fabric's motif on the same garment (4K).
// `data`: { imageUrl, fabricImageUrl?, patternScale, originalKitId?, createNewKit?, aspectRatio? }
export const resizeFabricPattern = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/resize-pattern`, data, token);

// Enhance texture — rebuild garment fabric so it matches the source swatch (Nano Banana Pro).
// `data`: { imageUrl, fabricImageUrl, originalKitId?, createNewKit?, aspectRatio? }
export const enhanceFabricTexture = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/enhance-texture`, data, token);

// Apply Partial Design Inspiration API (Gemini 2.5 Flash Image)
export const applyPartialDesignInspiration = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/partial-design-inspiration`, data, token);

// Apply Partial Sleeve Inspiration API (Replicate Nano Banana)
export const applyPartialSleeveInspiration = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/partial-sleeve-inspiration`, data, token);

export const applyPartialSilhouetteInspiration = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/magicedit/partial-silhouette-inspiration`, data, token);

export const generateBackSideView = (data, token) =>
  postQueuedGeneration(`${API_BASE_URL}/realtime/generate-back-view`, data, token);

// Public Catalogue APIs
export const createPublicCatalogue = (data, token) =>
  axios.post(`${API_BASE_URL}/public-catalogue/create`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getMyPublicCatalogues = (token, page = 1, limit = 10) =>
  axios.get(`${API_BASE_URL}/public-catalogue/my-catalogues?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getPublicCatalogue = (id, token) =>
  axios.get(`${API_BASE_URL}/public-catalogue/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updatePublicCatalogue = (id, data, token) =>
  axios.patch(`${API_BASE_URL}/public-catalogue/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deletePublicCatalogue = (id, token) =>
  axios.delete(`${API_BASE_URL}/public-catalogue/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getPublicCatalogueStats = (id, token) =>
  axios.get(`${API_BASE_URL}/public-catalogue/${id}/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Public route (no auth required)
export const getPublicCatalogueByPublicId = (publicId, password = null) => {
  const params = password ? { password } : {};
  return axios.get(`${API_BASE_URL}/public-catalogue/public/${publicId}`, { params });
};

// Subscription APIs
export const subscribeToCollection = (phoneNumber, token) =>
  axios.post(
    `${API_BASE_URL}/subscriptions`,
    { phoneNumber },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

export const getMySubscriptions = (token) =>
  axios.get(`${API_BASE_URL}/subscriptions`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getFollowersCount = (token) =>
  axios.get(`${API_BASE_URL}/subscriptions/followers-count`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const unsubscribeFromCollection = (subscriptionId, token) =>
  axios.delete(`${API_BASE_URL}/subscriptions/${subscriptionId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Admin Communication APIs (Firebase push + in-app notifications)
export const broadcastNotification = (title, message, token) =>
  axios.post(
    `${API_BASE_URL}/users/admin/broadcast`,
    { title, message },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const sendNotificationToUsers = (title, message, userIds, token) =>
  axios.post(
    `${API_BASE_URL}/users/admin/send-to-users`,
    { title, message, userIds },
    { headers: { Authorization: `Bearer ${token}` } }
  );

// Admin WhatsApp APIs (AiSensy)
export const getWhatsAppStatus = (token) =>
  axios.get(`${API_BASE_URL}/whatsapp/admin/status`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getAppSettings = (token) =>
  axios.get(`${API_BASE_URL}/app-settings`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateNanoBananaProvider = (provider, token) =>
  axios.patch(`${API_BASE_URL}/app-settings/nano-banana-provider`, { provider }, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getWhatsAppTemplates = (token) =>
  axios.get(`${API_BASE_URL}/whatsapp/admin/templates`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const createWhatsAppTemplate = (data, token) =>
  axios.post(`${API_BASE_URL}/whatsapp/admin/templates`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const updateWhatsAppTemplate = (id, data, token) =>
  axios.put(`${API_BASE_URL}/whatsapp/admin/templates/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const deleteWhatsAppTemplate = (id, token) =>
  axios.delete(`${API_BASE_URL}/whatsapp/admin/templates/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const getWhatsAppBroadcasts = (token, limit = 20) =>
  axios.get(`${API_BASE_URL}/whatsapp/admin/broadcasts`, {
    params: { limit },
    headers: { Authorization: `Bearer ${token}` }
  });

export const broadcastWhatsApp = (payload, token) =>
  axios.post(`${API_BASE_URL}/whatsapp/admin/broadcast`, payload, {
    headers: { Authorization: `Bearer ${token}` }
  });

export const sendWhatsAppToUsers = (payload, token) =>
  axios.post(`${API_BASE_URL}/whatsapp/admin/send-to-users`, payload, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Push Notification APIs
export const registerPushToken = (pushToken, token) =>
  axios.post(
    `${API_BASE_URL}/users/push-token`,
    { pushToken },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

export const removePushToken = (token) =>
  axios.delete(`${API_BASE_URL}/users/push-token`, {
    headers: { Authorization: `Bearer ${token}` }
  });

// Notify subscribers about a fabric
export const notifyFabricSubscribers = (productId, notificationType, token) =>
  axios.post(
    `${API_BASE_URL}/products/${productId}/notify-subscribers`,
    { notificationType },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

// Payments (Razorpay)
export const createRazorpayCreditsOrder = (packageId, token, couponCode) =>
  axios.post(
    `${API_BASE_URL}/payments/razorpay/order`,
    {
      purpose: 'credits',
      packageId,
      ...(couponCode ? { couponCode } : {}),
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const verifyRazorpayCreditsPayment = (
  { packageId, razorpay_order_id, razorpay_payment_id, razorpay_signature, couponCode },
  token
) =>
  axios.post(
    `${API_BASE_URL}/payments/razorpay/verify`,
    {
      purpose: 'credits',
      packageId,
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      ...(couponCode ? { couponCode } : {}),
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const createRazorpayPricingOrder = (planId, billingCycle, token, couponCode) =>
  axios.post(
    `${API_BASE_URL}/payments/razorpay/order`,
    {
      purpose: 'pricing',
      planId,
      billingCycle,
      ...(couponCode ? { couponCode } : {}),
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );

export const verifyRazorpayPricingPayment = (
  { planId, billingCycle, razorpay_order_id, razorpay_payment_id, razorpay_signature, couponCode },
  token
) =>
  axios.post(
    `${API_BASE_URL}/payments/razorpay/verify`,
    {
      purpose: 'pricing',
      planId,
      billingCycle,
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      ...(couponCode ? { couponCode } : {}),
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );